<template>
  <div
    ref="rootElement"
    class="base-combo-box"
    @focusout="onFocusOut"
  >
    <input
      ref="inputElement"
      v-model="keyword"
      class="jps-input jps-keyword-input is-size-6-touch"
      type="search"
      role="combobox"
      autocomplete="off"
      :placeholder="ls(placeholder, '')"
      :disabled="disabled"
      :aria-expanded="isOpen"
      :aria-controls="comboBoxId"
      aria-autocomplete="list"
      @focus="openComboBox"
      @input="onInput"
      @keydown="onKeydown"
      @compositionstart="onCompositionStart"
      @compositionend="onCompositionEnd"
    />

    <transition name="fade">
      <div
        ref="dropdownElement"
        class="select-option-wrapper">
        <ul
          v-if="isOpen"
          :id="comboBoxId"
          class="select-option-base"
          role="listbox"
        >
          <li
            v-for="(option, index) in filteredOptions"
            :id="`${componentId}-option-${index}`"
            :key="option.id"
            class="select-option"
            :class="{
              'select-option-active': index === activeIndex,
              'select-option-selected': option.id === modelValue
            }"
            role="option"
            :aria-selected="option.id === modelValue"
            @mouseenter="activeIndex = index"
            @mousedown.prevent="selectOption(option)"
          >
            <span>
              {{ ls(option.label) }}
            </span>
          </li>

          <li
            v-if="filteredOptions.length === 0"
            class="select-option"
            role="presentation"
          >
            {{ ls( { ja: '一致する値がありません', en: 'no match value.'} ) }}
          </li>
        </ul>
      </div>
    </transition>
  </div>
</template>


<script lang="ts">
export interface ComboBoxOptionValues {
  id: string
  label: LangString | LangStringYomi
}
</script>

<script lang="ts" setup>
import { createPopper } from '@popperjs/core'
import type { Instance } from '@popperjs/core'

const { ls } = useI18n()

interface Props {
  modelValue?: string
  placeholder?: LangString
  disabled?: boolean
  options: ComboBoxOptionValues[]
}
const props = withDefaults(defineProps<Props>(), {
  modelValue: '',
  disabled: false,
})
const emits = defineEmits(['update:modelValue'])

const componentId = luId()
const comboBoxId = `${componentId}-combo-box`

const keyword = ref('')
const isOpen = ref(false)
const isComposing = ref(false)
const activeIndex = ref(-1)

const normalizeSearchText = (value: string): string => {
  // 前後の空白除去、大文字・小文字の統一、カタカナとひらがなの統一を実施
  return value
    .normalize('NFKC')
    .trim()
    .toLocaleLowerCase()
    .replace(/[\u30a1-\u30f6]/g, character =>
      String.fromCharCode(character.charCodeAt(0) - 0x60)
    )
}

const selectedOption = computed(() => {
  return props.options.find(option => option.id === props.modelValue)
})

const filteredOptions = computed(() => {
  const normalizedKeyword = normalizeSearchText(keyword.value)
  if (!normalizedKeyword) {
    return props.options
  }

  // ID, ラベル(日本語、英語、よみ)に入力値と一致するものがあれば選択肢として返す
  return props.options.filter(option => {
    const searchableValues = [
      option.id,
      option.label.ja,
      option.label.en,
    ]
    if (option.label.yomi) {
      searchableValues.push(option.label.yomi)
    }
    return searchableValues.some(value =>
      normalizeSearchText(value).includes(normalizedKeyword)
    )
  })
})

const openComboBox = () => {
  if (props.disabled) {
    return
  }
  isOpen.value = true

  // 入力値と選択肢に一致するものがあれば、それを選択中として扱う
  const currentSelectedIndex = filteredOptions.value.findIndex(
    option => option.id === props.modelValue
  )
  activeIndex.value =
    currentSelectedIndex >= 0
      ? currentSelectedIndex
      : filteredOptions.value.length > 0
        ? 0
        : -1

  nextTick(async () => {
    await popperInstance.update()
  })
}

const closeComboBox = () => {
  isOpen.value = false
  activeIndex.value = -1
}

const selectOption = (option: any) => {
  keyword.value = ls(option.label)
  emits('update:modelValue', option.id)
  closeComboBox()
}

const clearSelectionIfNecessary = () => {
  const selected = selectedOption.value
  if (selected) {
    // 選択された値がちゃんとある場合
    emits('update:modelValue', selected.id)
  } else {
    // 選択肢にない自由な値が入力されている場合
    emits('update:modelValue', '')
  }
}

const onInput = () => {
  // IMEで日本語変換中などは処理しない
  if (isComposing.value) {
    return
  }
  // 入力が落ち着いたら選択中の値を確定させる
  clearSelectionIfNecessary()
  isOpen.value = true
  activeIndex.value = filteredOptions.value.length > 0 ? 0 : -1
}

const onCompositionStart = () => {
  isComposing.value = true
}

const onCompositionEnd = (event: CompositionEvent) => {
  isComposing.value = false
  keyword.value = (event.target as HTMLInputElement).value

  clearSelectionIfNecessary()
  isOpen.value = true
  activeIndex.value = filteredOptions.value.length > 0 ? 0 : -1
}

const isImeEvent = (event: KeyboardEvent): boolean => {
  return (
    isComposing.value ||
    event.isComposing ||
    event.keyCode === 229 // ブラウザ互換性のために229もチェックするが、最新ではisComposingのみでIME変換をとらえられる
  )
}

const moveActiveIndex = (direction: 1 | -1) => {
  const optionCount = filteredOptions.value.length
  if (optionCount === 0) {
    activeIndex.value = -1
    return
  }

  if (activeIndex.value < 0) {
    activeIndex.value = direction === 1 ? 0 : optionCount - 1
    return
  }
  activeIndex.value = (activeIndex.value + direction + optionCount) % optionCount

  nextTick(() => {
    const activeElement = document.getElementById(
      `${componentId}-option-${activeIndex.value}`
    )
    activeElement?.scrollIntoView({
      block: 'nearest'
    })
  })
}

const onKeydown = (event: KeyboardEvent) => {
  // IME変換中はキーイベントを監視しない
  if (isImeEvent(event)) {
    return
  }

  switch (event.key) {
    case 'ArrowDown':
      event.preventDefault()
      if (!isOpen.value) {
        openComboBox()
      } else {
        moveActiveIndex(1)
      }
      break

    case 'ArrowUp':
      event.preventDefault()
      if (!isOpen.value) {
        openComboBox()
      } else {
        moveActiveIndex(-1)
      }
      break

    case 'Enter':
      if (
        isOpen.value &&
        activeIndex.value >= 0 &&
        filteredOptions.value[activeIndex.value]
      ) {
        event.preventDefault()
        selectOption(filteredOptions.value[activeIndex.value])
      }
      break

    case 'Escape':
      if (isOpen.value) {
        event.preventDefault()
        closeComboBox()
      }
      break

    case 'Tab':
      closeComboBox()
      break
  }
}

const rootElement = ref<HTMLElement>(null)
const onFocusOut = async () => {
  await nextTick()

  const focusedElement = document.activeElement
  if (
    rootElement.value &&
    focusedElement instanceof Node &&
    !rootElement.value.contains(focusedElement)
  ) {
    closeComboBox()
  }
}

const updateKeyword = () => {
  const selected = selectedOption.value
  if (selected) {
    keyword.value = ls(selectedOption.value.label)
  }
}

watch(
  // 親からくる入力値が変わった場合の処理
  () => props.modelValue,
  () => updateKeyword(),
  { immediate: true }
)
watch(
  // 親からくる選択肢が変わった場合の再描画
  () => props.options,
  async () => {
    updateKeyword()
    if (activeIndex.value >= filteredOptions.value.length) {
      activeIndex.value =
        filteredOptions.value.length > 0
          ? filteredOptions.value.length - 1
          : -1
    }
  },
  { deep: true, immediate: true }
)
watch(
  // キーワードを空にされた場合に、選択を一旦クリアする処理
  () => keyword.value,
  (v) => {
    if (!v || v === '') {
      activeIndex.value = -1
      emits('update:modelValue', null)
      // 入力から要素選択して、フォーカスアウトせずに×で入力消した時用の強制書き換え
      requestAnimationFrame(() => {
        popperInstance?.forceUpdate()
      })
    }
  }
)

// ドロップダウンリストの表示位置調整用
onMounted(async () => {
  await createPopperInstance()
})

const inputElement = ref<HTMLInputElement>(null)
const dropdownElement = ref<HTMLInputElement>(null)
let popperInstance: Instance | null = null
const createPopperInstance = async () => {
  await nextTick()
  if (!inputElement.value || !dropdownElement.value) {
    return
  }

  // popperInstanceがあったら削除してから作成する
  popperInstance?.destroy()
  popperInstance = createPopper(
    inputElement.value,
    dropdownElement.value,
    {
      placement: 'bottom-start',
      strategy: 'fixed',
      modifiers: [
        {
          name: 'offset',
          options: {
            offset: [0, 4]
          }
        },
        {
          name: 'flip',
          options: {
            fallbackPlacements: [
              'top-start',
              'bottom-end',
              'top-end'
            ],
            padding: 8
          }
        },
        {
          name: 'preventOverflow',
          options: {
            boundary: 'clippingParents',
            rootBoundary: 'viewport',
            padding: 8,
            mainAxis: true,
            altAxis: true,
            tether: true
          }
        },
        {
          name: 'sameWidth',
          enabled: true,
          phase: 'beforeWrite',
          requires: ['computeStyles'],
          fn({ state }) {
            state.styles.popper.minWidth =
              `${state.rects.reference.width}px`
          },
          effect({ state }) {
            state.elements.popper.style.minWidth =
              `${state.elements.reference.getBoundingClientRect().width}px`
            return () => {
              state.elements.popper.style.minWidth = ''
            }
          }
        }
      ]
    }
  )
  await popperInstance.update()
}
// 表示する選択肢が絞り込み等によって変更された場合は、ドロップダウンの表示位置も更新する
watch(
  filteredOptions,
  async () => {
    if (!isOpen.value) {
      return
    }
    await nextTick()
    await popperInstance?.update()
  },
  { flush: 'post' }
)
// 要素が消える際にpopperを削除しておく
onBeforeUnmount(() => {
  popperInstance?.destroy()
  popperInstance = null
})
</script>

<style lang="scss">
@use '@/assets/styles/scss/vue' as *;

.base-combo-box {
  position: relative;

  .select-option-wrapper {
    position: absolute;
    z-index: 999;
    width: max-content;
    max-width: calc(100vw - 16px);

    .select-option-base {
      display: flex;
      flex-direction: column;
      filter: drop-shadow(0 4px 3px rgb(0 0 0 / 0.07)) drop-shadow(0 2px 2px rgb(0 0 0 / 0.06));

      overflow: auto;
      max-height: 50dvh;

      border-radius: 0.25rem;
      border-width: 1px;
      border-color: #c1c1c1;
      border-style: solid;
      background-color: $white;

      .select-option {
        position: relative;
        display: block;
        text-align: left;
        line-height: 1.5;
        width: 100%;
        padding: 0.5rem;
        cursor: pointer;
      }

      .select-option-active {
        background-color: $jps-gray-200;
      }

      .select-option-selected {
        font-weight: 700;
      }
    }
  }
}
</style>